/**
 * FlagIcon - drapeaux SVG inline cross-browser.
 *
 * Pourquoi : Windows / Edge ne rendent PAS les emoji flags (decision Microsoft
 * de ne pas inclure les regional indicator pairs comme glyphs drapeaux dans
 * Segoe UI Emoji). Resultat : 🇧🇪 s affiche comme "BE" en texte simple sur
 * tous les browsers Windows. Solution = SVG inline.
 *
 * v2026.06.113
 */
import { CSSProperties } from 'react';

type CountryCode = 'be' | 'fr' | 'en' | 'gb' | 'de' | 'nl' | 'eu';

interface Props {
  code:   CountryCode;
  size?:  number;       // default 14
  style?: CSSProperties;
  title?: string;       // alt text accessibility
}

// Aspect ratio 3:2 pour les drapeaux pays (sauf BE qui est ~13:15 mais on
// simplifie en 3:2 pour la cohérence visuelle).
export function FlagIcon({ code, size = 14, style, title }: Props) {
  const w = size;
  const h = Math.round(size * 0.7);
  const common: CSSProperties = {
    display: 'inline-block',
    verticalAlign: '-2px',
    borderRadius: 2,
    boxShadow: '0 0 0 0.5px rgba(0,0,0,0.15)',
    ...style,
  };
  const a = title;

  switch (code) {
    case 'be':
      return (
        <svg width={w} height={h} viewBox="0 0 30 20" xmlns="http://www.w3.org/2000/svg" style={common} aria-label={a}>
          <rect x="0"  y="0" width="10" height="20" fill="#000000" />
          <rect x="10" y="0" width="10" height="20" fill="#FAE042" />
          <rect x="20" y="0" width="10" height="20" fill="#ED2939" />
        </svg>
      );
    case 'fr':
      return (
        <svg width={w} height={h} viewBox="0 0 30 20" xmlns="http://www.w3.org/2000/svg" style={common} aria-label={a}>
          <rect x="0"  y="0" width="10" height="20" fill="#002395" />
          <rect x="10" y="0" width="10" height="20" fill="#FFFFFF" />
          <rect x="20" y="0" width="10" height="20" fill="#ED2939" />
        </svg>
      );
    case 'en':
    case 'gb':
      // Union Jack simplifie (sans les diagonales fines de St-Patrick pour rester lisible a 14px)
      return (
        <svg width={w} height={h} viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" style={common} aria-label={a}>
          <rect width="60" height="30" fill="#012169" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" strokeWidth="6" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2" />
          <path d="M30,0 V30 M0,15 H60" stroke="#FFFFFF" strokeWidth="10" />
          <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
        </svg>
      );
    case 'de':
      return (
        <svg width={w} height={h} viewBox="0 0 30 20" xmlns="http://www.w3.org/2000/svg" style={common} aria-label={a}>
          <rect x="0" y="0"  width="30" height="6.67" fill="#000000" />
          <rect x="0" y="6.67" width="30" height="6.67" fill="#DD0000" />
          <rect x="0" y="13.33" width="30" height="6.67" fill="#FFCE00" />
        </svg>
      );
    case 'nl':
      return (
        <svg width={w} height={h} viewBox="0 0 30 20" xmlns="http://www.w3.org/2000/svg" style={common} aria-label={a}>
          <rect x="0" y="0"  width="30" height="6.67" fill="#AE1C28" />
          <rect x="0" y="6.67" width="30" height="6.67" fill="#FFFFFF" />
          <rect x="0" y="13.33" width="30" height="6.67" fill="#21468B" />
        </svg>
      );
    case 'eu':
      // Drapeau Europe : cercle de 12 etoiles or sur fond bleu (simplifie en 1 etoile centrale)
      return (
        <svg width={w} height={h} viewBox="0 0 30 20" xmlns="http://www.w3.org/2000/svg" style={common} aria-label={a}>
          <rect width="30" height="20" fill="#003399" />
          {/* 12 etoiles en cercle - simplifie en cercle de points dores */}
          {Array.from({ length: 12 }).map((_, i) => {
            const angle = (i * 30) * Math.PI / 180;
            const cx = 15 + Math.cos(angle - Math.PI / 2) * 6;
            const cy = 10 + Math.sin(angle - Math.PI / 2) * 6;
            return <circle key={i} cx={cx} cy={cy} r={1} fill="#FFCC00" />;
          })}
        </svg>
      );
    default:
      return null;
  }
}

/**
 * Helper : convertit un code locale i18n en code drapeau.
 *   'fr-BE' -> 'fr'  (langue francaise, on prend le drapeau FR pas BE)
 *   'en'    -> 'en'
 *   'nl-BE' -> 'nl'
 *   'de-BE' -> 'de'
 */
export function localeToFlag(locale: string): CountryCode {
  const lng = locale.toLowerCase();
  if (lng.startsWith('fr')) return 'fr';
  if (lng.startsWith('nl')) return 'nl';
  if (lng.startsWith('de')) return 'de';
  if (lng.startsWith('en')) return 'en';
  return 'eu';
}
